import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import type { LpfrAdminItemDto, LpfrPlatformDto } from "@finora/api-client";
import { Button, EmptyState, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";
import { useState } from "react";
import { LpfrDevicesTab } from "./LpfrDevicesTab";

const PLAT: Record<LpfrPlatformDto, { label: string; cls: string }> = {
  WINDOWS: { label: "Windows", cls: "bg-blue-100 text-blue-800" },
  LINUX: { label: "Linux", cls: "bg-amber-100 text-amber-800" },
  ANDROID: { label: "Android", cls: "bg-green-100 text-green-800" },
};

function fmt(v: string | null): string {
  return v ? new Date(v).toLocaleString("sr-Latn-RS", { dateStyle: "short", timeStyle: "short" }) : "—";
}

function Card({ item }: { item: LpfrAdminItemDto }) {
  return (
    <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-slate-900">{item.companyName}</p>
          <p className="text-xs text-slate-500">PIB {item.taxId}</p>
        </div>
        <div className="flex flex-wrap gap-1">
          {item.platforms.length === 0 && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">platforma nepoznata</span>}
          {item.platforms.map((p) => (
            <span key={p} className={`rounded-full px-2 py-0.5 text-xs font-medium ${PLAT[p].cls}`}>{PLAT[p].label}</span>
          ))}
        </div>
      </div>
      <div className="mt-3 grid gap-3 text-sm text-slate-700 md:grid-cols-3">
        <div>
          <p className="text-xs uppercase text-slate-400">Licence</p>
          {item.licenses.length === 0 ? "—" : item.licenses.map((l) => (
            <p key={l.keyPrefix}>
              <code className="text-xs">{l.keyPrefix}…</code> {l.label ?? ""} {l.revoked && <span className="text-red-600">(opozvana)</span>}
            </p>
          ))}
        </div>
        <div>
          <p className="text-xs uppercase text-slate-400">Preuzimanja</p>
          <p>Windows: {item.windows.count}{item.windows.count > 0 && ` · v${item.windows.version ?? "?"} · ${fmt(item.windows.last)}`}</p>
          <p>Linux: {item.linux.count}{item.linux.count > 0 && ` · v${item.linux.version ?? "?"} · ${fmt(item.linux.last)}`}</p>
        </div>
        <div>
          <p className="text-xs uppercase text-slate-400">Potpisani računi</p>
          <p>Ukupno: {item.invoices.total} (poslednji {fmt(item.invoices.last)})</p>
          <p>Android kasa: {item.invoices.viaAndroid} · računar: {item.invoices.viaComputer}</p>
          {item.invoices.androidDevices.length > 0 && <p className="text-xs text-slate-500">Uređaji: {item.invoices.androidDevices.join(", ")}</p>}
          {item.invoices.jids.length > 0 && <p className="text-xs text-slate-500">JID: {item.invoices.jids.join(", ")}</p>}
        </div>
      </div>
    </div>
  );
}

export function LpfrAdminPage() {
  const [tab, setTab] = useState<"firme" | "uredjaji">("firme");
  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Button variant={tab === "firme" ? "primary" : "secondary"} onClick={() => setTab("firme")}>Po firmama</Button>
        <Button variant={tab === "uredjaji" ? "primary" : "secondary"} onClick={() => setTab("uredjaji")}>Uređaji</Button>
      </div>
      {tab === "firme" ? <LpfrFirmeView /> : <LpfrDevicesTab />}
    </div>
  );
}

function LpfrFirmeView() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["lpfr-admin"], queryFn: () => api.getLpfrAdminOverview() });
  if (q.isLoading) return <Skeleton className="h-40 w-full" />;
  if (q.isError || !q.data) return <EmptyState icon={ShieldCheck} title="Greška" description="Pregled L-PFR-a nije dostupan." />;
  const d = q.data;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold text-slate-900">L-PFR po firmama</h1>
        <Button variant="secondary" onClick={() => qc.invalidateQueries({ queryKey: ["lpfr-admin"] })}>Osveži</Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {[
          ["Firmi", d.totals.companies],
          ["Windows", d.totals.windows],
          ["Linux", d.totals.linux],
          ["Android", d.totals.android],
          ["Potpisanih računa", d.totals.invoices],
          ["Odbijena preuzimanja", d.totals.deniedDownloads],
        ].map(([label, value]) => (
          <div key={String(label)} className="rounded-xl border border-border bg-surface p-3 shadow-subtle">
            <p className="text-xs text-slate-500">{label}</p>
            <p className="text-lg font-semibold text-slate-900">{value}</p>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-500">
        Windows i Linux se prepoznaju po preuzimanju, a Android po računima koje je potpisala Android kasa. Prava prijava instalacije (da je L-PFR pokrenut) dolazi u sledećoj fazi.
      </p>

      {d.items.length === 0 ? (
        <EmptyState icon={ShieldCheck} title="Još nema firmi sa L-PFR-om" description="Firma se pojavljuje kad preuzme L-PFR ili potpiše prvi račun." />
      ) : (
        <div className="space-y-3">{d.items.map((i) => <Card key={i.companyId} item={i} />)}</div>
      )}

      <h2 className="pt-2 text-base font-semibold text-slate-900">Poslednja preuzimanja</h2>
      <div className="overflow-x-auto rounded-xl border border-border bg-surface shadow-subtle">
        <table className="w-full min-w-[700px] text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase text-slate-500">
              <th className="px-3 py-2">Vreme</th><th className="px-3 py-2">Firma</th><th className="px-3 py-2">Licenca</th>
              <th className="px-3 py-2">Platforma</th><th className="px-3 py-2">Verzija</th><th className="px-3 py-2">IP</th><th className="px-3 py-2">Ishod</th>
            </tr>
          </thead>
          <tbody>
            {d.recent.map((r) => (
              <tr key={r.id} className="border-b border-border last:border-0">
                <td className="px-3 py-2 whitespace-nowrap">{fmt(r.createdAt)}</td>
                <td className="px-3 py-2">{r.companyName}</td>
                <td className="px-3 py-2"><code className="text-xs">{r.keyPrefix ?? "—"}</code></td>
                <td className="px-3 py-2">{r.platform}</td>
                <td className="px-3 py-2">{r.version ?? "—"}</td>
                <td className="px-3 py-2">{r.ip ?? "—"}</td>
                <td className="px-3 py-2">{r.allowed ? <span className="text-green-700">dozvoljeno</span> : <span className="text-red-600">odbijeno</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
